import { useEffect, useMemo, useState } from "react";
import Dataset from "datasets/types/interfaces/dataset";
import { AlertCircle, Folder, Search } from "lucide-react";
import { mutate } from "swr";
import { Checkbox } from "~/components/ui/checkbox";
import useToast from "~/components/ui/toast";
import { useFetcher } from "~/hooks/useFetcher";
import { cn } from "~/lib/utils";
import { FormDialog } from "~/pages/manifold-nextgen/components/FormDialog";
import { useAsyncMutation } from "~/pages/manifold-nextgen/hooks/useAsyncMutation";
import { projectDatasetsPath, setProjectDatasets } from "~/services/projects";

const { VITE_CATALOG_API } = import.meta.env;
const CATALOG_DATASETS_URL = `${VITE_CATALOG_API}/datasets?offset=0&limit=1000&sort_by=title&sort_order=asc`;

interface ProjectDatasetsDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attachedIds: string[];
}

export const ProjectDatasetsDialog = ({
  projectId,
  open,
  onOpenChange,
  attachedIds,
}: ProjectDatasetsDialogProps) => {
  const { showSuccess } = useToast();
  const { data, error, isLoading } = useFetcher<Dataset[]>(open ? CATALOG_DATASETS_URL : null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (open) {
      setSelected(new Set());
      setSearch("");
    }
  }, [open]);

  const attachedSet = useMemo(() => new Set(attachedIds), [attachedIds]);

  const filtered = useMemo(() => {
    const items = (data ?? []).filter((d) => !attachedSet.has(d.id));
    const q = search.trim().toLowerCase();
    if (!q) {
      return items;
    }
    return items.filter(
      (d) => d.title.toLowerCase().includes(q) || (d.description ?? "").toLowerCase().includes(q)
    );
  }, [data, attachedSet, search]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const { execute, isPending } = useAsyncMutation(
    (ids: string[]) => setProjectDatasets(projectId, ids),
    {
      errorMessage: "Failed to add datasets",
      onSuccess: async (result) => {
        await mutate(projectDatasetsPath(projectId));
        const added = result.length - attachedIds.length;
        showSuccess(`${added} dataset${added === 1 ? "" : "s"} added`);
        onOpenChange(false);
      },
    }
  );

  const handleSave = () => {
    const newIds = [...attachedIds, ...Array.from(selected)];
    void execute(newIds);
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add Datasets"
      description="Select datasets to add to this project."
      contentClassName="sm:max-w-[640px]"
      onPrimary={handleSave}
      isPending={isPending}
      primaryLabel="Add"
      primaryPendingLabel="Adding…"
      primaryDisabled={selected.size === 0}
    >
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search datasets..."
          aria-label="Search datasets"
          className={cn(
            "h-9 w-full rounded-md border border-input bg-transparent pl-9 pr-3 py-1",
            "text-sm placeholder:text-muted-foreground shadow-xs",
            "focus-visible:outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
          )}
        />
      </div>

      <div className="-mx-2 flex h-[50vh] flex-col overflow-y-auto">
        {isLoading && (
          <div role="status" className="flex flex-col gap-2 px-2 py-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-14 animate-pulse rounded-md bg-muted" />
            ))}
          </div>
        )}

        {!isLoading && error && (
          <div
            role="alert"
            className="flex flex-col items-center gap-2 px-2 py-8 text-center text-destructive"
          >
            <AlertCircle className="size-6" aria-hidden="true" />
            <p className="text-sm font-medium">Failed to load datasets</p>
          </div>
        )}

        {!isLoading && !error && filtered.length === 0 && (
          <p className="px-2 py-8 text-center text-sm text-muted-foreground">
            {search ? "No datasets match your search." : "No more datasets to add."}
          </p>
        )}

        {!isLoading && !error && filtered.length > 0 && (
          <ul className="flex flex-col" role="list">
            {filtered.map((dataset) => (
              <DatasetRow
                key={dataset.id}
                dataset={dataset}
                checked={selected.has(dataset.id)}
                onToggle={() => toggle(dataset.id)}
              />
            ))}
          </ul>
        )}
      </div>
    </FormDialog>
  );
};

interface DatasetRowProps {
  dataset: Dataset;
  checked: boolean;
  onToggle: () => void;
}

const DatasetRow = ({ dataset, checked, onToggle }: DatasetRowProps) => {
  const checkboxId = `dataset-row-${dataset.id}`;
  return (
    <li>
      <label
        htmlFor={checkboxId}
        className={cn(
          "flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 transition-colors",
          "hover:bg-accent/40"
        )}
      >
        <Folder className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">{dataset.title}</p>
          <p className="truncate text-xs text-muted-foreground">
            {dataset.description || "No description"}
          </p>
        </div>
        <Checkbox id={checkboxId} checked={checked} onCheckedChange={onToggle} />
      </label>
    </li>
  );
};
